<template>
  <div>
    <ContentSectionLoader v-if="!loaded" />
    <Transition name="fade">
      <div v-if="loaded">
        <div
          v-if="membership && !membershipHasBeenDeleted"
          class="text-center"
        >
          <h2>
            <span v-text="membership.attributes.full_name_concatenated" /> &ndash; {{ membership.attributes.email }}
          </h2>
          <div
            v-if="membership.membershipTitles.data.length"
            class="flex flex-col justify-center"
          >
            <MembershipTitle
              v-for="title in membershipTitles"
              :key="title.label"
              :membership-title="title"
              class="m-0"
            />
          </div>
          <ul
            v-if="!membershipHasBeenDeactivated"
            class="list-reset"
          >
            <li>
              <strong v-text="'Membership status:'" /> {{ status }}
            </li>
            <li>
              <strong v-text="'Work status:'" /> {{ workStatus }}
            </li>
            <li v-if="claimedAt">
              <strong v-text="'Registered:'" /> {{ claimedAt | calendarTime }}
            </li>
            <li v-if="importedAt">
              <strong v-text="'Imported:'" /> {{ importedAt | calendarTime }}
            </li>
            <li v-if="invitedAt">
              <strong v-text="'Last invited:'" /> {{ invitedAt | calendarTime }}
            </li>
            <li v-if="nextPaymentDueDate">
              <strong v-text="'Next Payment:'" /> {{ nextPaymentDueDate }}
            </li>
          </ul>
        </div>
        <div
          v-if="membership && !membershipHasBeenDeleted"
          v-can="'edit-profiles'"
          class="max-w-xs mx-auto flex items-baseline justify-center my-6"
        >
          <button
            v-if="!membershipHasBeenDeactivated && status !== 'Active'"
            type="button"
            class="btn btn--sm mr-6 btn--warning"
            @click.prevent="$store.commit('membershipOpenInviteMembershipModal')"
            v-text="invitationButtonText"
          />
          <button
            v-if="!membershipHasBeenDeactivated"
            type="button"
            class="btn btn--sm mr-6 btn--warning"
            @click="$store.commit('membershipOpenDeactivateMembershipModal', membership)"
            v-text="'Deactivate'"
          />
          <button
            v-if="membershipHasBeenDeactivated"
            type="button"
            class="btn btn--sm mx-6 btn--warning"
            @click="$store.commit('membershipOpenReactivateMembershipModal', membership)"
            v-text="'Reactivate'"
          />
          <button
            type="button"
            class="btn btn--sm btn--danger"
            @click="$store.commit('membershipOpenDeleteMembershipModal', membership)"
            v-text="'Delete'"
          />
        </div>
        <div
          v-if="membershipHasBeenDeleted"
          class="card text-center bg-white max-w-sm mx-auto p-8"
        >
          <h1 v-text="'Alert'" />
          <p v-text="'This membership has been deleted.'" />
        </div>
        <BaseTabInterface
          v-if="!membershipHasBeenDeleted && !membershipHasBeenDeactivated"
          :initial-tab="filteredTabs[0]"
          :tabs="filteredTabs"
        >
          <div
            slot-scope="{ tabs, activeTab, changeActiveTab }"
            class="tabs"
          >
            <ul class="tabs__tab md:inline-flex hidden md:block">
              <BaseTab
                v-for="(tab, index) in tabs"
                :key="tab.slug"
              >
                <li
                  :class="{ 'current': activeTab === tab.slug }"
                  class="w-full md:w-auto"
                  tabindex="0"
                  @keydown.enter.prevent="changeActiveTab(tab.slug)"
                >
                  <a
                    :id="`tab-${tab.slug}`"
                    :aria-selected="activeTab === tab.slug"
                    @click="changeActiveTab(tab.slug)"
                    v-text="`${index + 1}. ${tab.title}`"
                  />
                </li>
              </BaseTab>
            </ul>
            <div class="md:hidden select-container">
              <select @change="changeActiveTab($event.target.value)">
                <option 
                  v-for="(tab, index) in tabs"
                  :key="tab.slug"
                  :selected="activeTab === tab.slug"
                  :value="tab.slug"
                  v-text="`${index + 1}. ${tab.title}`"
                />
              </select>
            </div>
            <div class="tabs__tab_content py-8">
              <BaseTabContent
                :active-tab="activeTab"
                tab-id="information"
              >
                <MembershipForm
                  admin-mode
                  :membership="membership"
                />
              </BaseTabContent>
              <BaseTabContent
                v-if="$root.userCan('edit-profiles')"
                :active-tab="activeTab"
                tab-id="groups"
              >
                <MembershipGroups :membership-uuid="membershipUuid" />
              </BaseTabContent>
              <BaseTabContent
                v-if="$root.userCan('edit-profiles')"
                :active-tab="activeTab"
                tab-id="permissions"
              >
                <MembershipAbilities
                  :membership-uuid="membershipUuid"
                  context="editing"
                />
              </BaseTabContent>
              <BaseTabContent
                v-if="paymentsEnabled && $root.userCan('edit-profiles')"
                :active-tab="activeTab"
                tab-id="fee"
              >
                <ConfigureMembershipFee
                  :membership-uuid="membershipUuid"
                  context="editing"
                />
              </BaseTabContent>
            </div>
          </div>
        </BaseTabInterface>
      </div>
    </Transition>
    <DeactivateMembershipModal @membershipHasBeenDeactivated="fetch" />
    <ReactivateMembershipModal @membershipHasBeenReactivated="fetch" />
    <DeleteMembershipModal @membershipHasBeenDeleted="membershipHasBeenDeleted = true" />
    <InviteMembershipModal
      :email="membership.attributes.email"
      :full-name="membership.attributes.full_name"
      :uuid="membershipUuid"
      @membershipHasBeenInvited="fetch"
    />
  </div>
</template>

<script type="text/babel">
import { mapActions, mapState } from 'vuex'
import MembershipGroups from 'Membership/Resources/assets/js/components/MembershipGroups'
import MembershipAbilities from 'Membership/Resources/assets/js/components/MembershipAbilities'
import MembershipForm from 'Membership/Resources/assets/js/components/profiles/MembershipForm'
import DeleteMembershipModal from 'Membership/Resources/assets/js/components/_modals/DeleteMembershipModal'
import InviteMembershipModal from 'Membership/Resources/assets/js/components/_modals/InviteMembershipModal'
import DeactivateMembershipModal from 'Membership/Resources/assets/js/components/_modals/DeactivateMembershipModal'
import ReactivateMembershipModal from 'Membership/Resources/assets/js/components/_modals/ReactivateMembershipModal'
import BaseTab from 'Base/Resources/assets/js/components/BaseTab'
import BaseTabContent from 'Base/Resources/assets/js/components/BaseTabContent'
import MembershipTitle from 'Membership/Resources/assets/js/components/_partials/MembershipTitle'
import ConfigureMembershipFee from 'Membership/Resources/assets/js/components/ConfigureMembershipFee'

export default {
  name: 'MembershipEditor',
  components: {
    BaseTab,
    BaseTabContent,
    MembershipGroups,
    MembershipAbilities,
    InviteMembershipModal,
    MembershipForm,
    DeleteMembershipModal,
    DeactivateMembershipModal,
    ReactivateMembershipModal,
    MembershipTitle,
    ConfigureMembershipFee,
  },
  props: {
    membershipUuid: {
      type: String,
      required: true,
    },
    profileSharingIsEnabled: {
      type: Boolean,
      required: true,
    },
    canGroupAdminEditMembershipProfile: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      loaded: false,
      membershipHasBeenDeleted: false,
      membership: {
        attributes: {
          full_name: '',
          email: '',
        },
      },
      availableTabs: [
        {
          title: 'Member Information',
          slug: 'information',
        },
        {
          title: 'Groups',
          slug: 'groups',
        },
        {
          title: 'User Permissions',
          slug: 'permissions',
        },
      ],
    }
  },
  computed: {
    ...mapState({
      membershipFee: state => state.payments.membershipFee,
    }),
    membershipHasBeenDeactivated() {
      return this.membership.attributes.deactivated
    },
    status() {
      return _.upperFirst(this.membership.attributes.status)
    },
    workStatus() {
      let workStatus = this.membership.attributes.work_status

      const statusesToAlter = {
        active: 'active_member',
        auxiliary: 'auxiliary_member',
        retired: 'retired_member',
      }

      if (statusesToAlter.hasOwnProperty(this.membership.attributes.work_status)) {
        workStatus = statusesToAlter[this.membership.attributes.work_status]
      }

      return _.startCase(workStatus.replace('_', ' '))
    },
    claimedAt() {
      return this.membership.attributes.claimed_at
    },
    importedAt() {
      return this.membership.attributes.imported_at
    },
    invitedAt() {
      return this.membership.attributes.invited_at
    },
    invitationButtonText() {
      return this.membership.attributes.invited_at ? 'Invite again' : 'invite'
    },
    membershipTitles() {
      return this.membership.membershipTitles.data
    },
    paymentsEnabled() {
      return this.$root.Union.payments_enabled
    },
    filteredTabs() {
      if (this.$root.userCan('edit-profiles')) {
        return this.availableTabs
      }

      if (this.canGroupAdminEditMembershipProfile) {
        return [
          {
            title: 'Member Information',
            slug: 'information',
          },
        ]
      }

      return []
    },
    nextPaymentDueDate() {
      return this.membership.subscription?.attributes
        ? this.$date.toMomentDateFormat(this.membership.subscription.attributes.current_period_end * 1000)
        : null
    },
  },
  async mounted() {
    await this.$nextTick()

    this.fetch()

    if (this.paymentsEnabled) {
      this.getMembershipFee().then(() => {
        this.availableTabs.push({
          title: `${this.membershipFee.attributes.title} Configuration`,
          slug: 'fee',
        })
      })
    }
  },
  methods: {
    ...mapActions({
      getMembershipFee: 'paymentsGetMembershipFee',
    }),
    fetch() {
      this.loaded = false

      this.$api.active().get('memberships/' + this.membershipUuid +
        '?include=union,subscription,membershipTitles,addresses,phoneNumbers,emergencyContacts,customMembershipFields,membershipGroups')
        .then((response) => {
          this.membership = response.data
          this.loaded = true
        })
    },
  },
}
</script>
